/**
 * The run pacing math (Depths rework). Three monotonic pools, never conflate:
 * - barFill (kill-fed) drives the 5 pick thresholds AND the Summon Bar
 * - gemsEarned (collection-fed) drives pickup stat bumps and the run-end tithe
 * - gemsHeld is the spendable wallet (shops)
 */

/**
 * Kill-gem normalizer: the ×2 density rework doubled kill counts, so per-kill
 * gem value halves at the killEnemy funnel to hold the run economy where it
 * was tuned. Boss/Keeper are exempt (their counts didn't double). Producer-
 * side only — pickup thresholds, tithe, and every gem cost stay untouched.
 */
export const KILL_GEM_NORM = 0.5;

/** Gem value multiplier per floor: descend = earn faster (design §3).
 *  Reshaped 2026-07-28 (Sarah's playtests, two rounds): floor 1 up then
 *  trimmed to 1.4 ("a bit too good" at 1.5); floors 2–3 confirmed right;
 *  deep growth flattened hard to +0.5/floor (4+ was awash even after the
 *  first flattening, and the wayside merchants are gone now too).
 *  killFillMult below deliberately KEEPS the old shape — bar pacing and
 *  gem economy tune independently. */
export function gemMultiplier(floor: number): number {
  if (floor === 1) return 1.4;
  if (floor === 2) return 2.5;
  if (floor === 3) return 3.5;
  return 3.5 + 0.5 * (floor - 3);
}

/**
 * Kill-bar fill multiplier per floor. Same shape as gemMultiplier but a
 * SEPARATE function: bar pacing and gem economy must tune independently.
 * Deeper kills fill faster — diving is the only way to buy tempo.
 */
export function killFillMult(floor: number): number {
  if (floor === 1) return 1;
  if (floor === 2) return 2;
  if (floor === 3) return 3.5;
  return 3.5 + 1.5 * (floor - 3);
}

/**
 * Kill-bar: full at this many fill points (boss summonable). Regulars grant
 * REGULAR_FILL × killFillMult(floor); elites (and the Keeper) ELITE_FILL ×.
 * The boss itself grants none — its kill IS the payoff. Enrage kills fill at
 * full rate (overtime kills still feed the bar; the ramp is the counterweight).
 */
export const KILL_BAR_FULL = 1000;
// 0.5, not 1: the ×2 density rework doubled the horde but held the bar at
// 1000 — per-kill fill halves so pick/summon pacing stays where it was tuned
// (flat bonus fills like Knell keep their relative power on a fixed bar).
export const REGULAR_FILL = 0.5;
// 7, not 8: with the finite spawn budget, a 100%-clear floor 2 (mites and
// an ambush included) must land just UNDER the bar (I1 in spawnBudget.test).
export const ELITE_FILL = 7;

/**
 * The 5 pick thresholds, as fractions of the bar. Front-loaded — gaps of
 * 5/7/9/11/13% — with the fifth landing well before the bar completes:
 * build identity is established before the deep floors, not confirmed after.
 */
export const PICK_FRACTIONS = [0.05, 0.12, 0.21, 0.32, 0.45] as const;

/** How many of the 5 picks a given barFill has crossed. */
export function picksEarned(barFill: number): number {
  let n = 0;
  for (const f of PICK_FRACTIONS) if (barFill >= f * KILL_BAR_FULL) n++;
  return n;
}

export function summonBarFill(barFill: number): number {
  return Math.min(1, barFill / KILL_BAR_FULL);
}

/**
 * Whole-run wall clock (sim seconds; RunState.timeSeconds, ticks in the
 * vault too). Past this the dark enrages: escalating spawn pressure until
 * the player dies or summons the boss (the fight is the escape hatch).
 */
export const RUN_TIME_LIMIT_SECONDS = 900;
